import config from '@/config';

/**
 * Connexion Okta, menée par l'API (App\Controller\OktaSsoController) ; la session reste dans le localStorage :
 *  1. startOktaLogin : le navigateur part sur /sso/okta/start avec l'empreinte S256 d'un secret (verifier) gardé dans
 *     le sessionStorage de l'onglet
 *  2. Okta, puis l'API, renvoient sur /sso/callback?code=… (code à usage unique, 60 s) ou sur /login?sso_error=…
 *  3. SsoCallbackPage échange le code et le verifier contre la session (POST /sso/okta/token, useAuthStore.loginWithOkta).
 *     Sans le verifier de cet onglet, le code ne sert à rien : un lien de retour envoyé par un tiers ne connecte personne
 */

const VERIFIER_STORAGE_KEY = 'teiji.sso.verifier';

/** Raisons d'échec renvoyées par l'API (App\Security\Sso\SsoException) */
const SSO_ERROR_MESSAGES: Record<string, string> = {
  unknown_account: "Aucun compte Teiji n'est associé à votre compte Okta.\nContactez votre administrateur.",
  disabled: 'Votre compte Teiji est désactivé.\nContactez votre administrateur.',
};
const SSO_FAILED_MESSAGE = 'La connexion avec Okta a échoué. Réessayez.';

/** Bouton « Se connecter avec Okta » : seulement si Okta est configuré pour l'environnement (appConfig.js) */
export function isOktaEnabled(appConfig: AppConfig = config): boolean {
  return !!appConfig.oktaEnabled;
}

/** Message d'un échec de connexion SSO (?sso_error=… de la page de connexion), ou null */
export function ssoErrorMessage(reason: string | null): string | null {
  if (!reason) return null;
  return SSO_ERROR_MESSAGES[reason] ?? SSO_FAILED_MESSAGE;
}

export async function startOktaLogin(): Promise<void> {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  sessionStorage.setItem(VERIFIER_STORAGE_KEY, verifier);
  const challenge = await pkceChallenge(verifier);

  window.location.assign(`${config.apiUrl}/sso/okta/start?${new URLSearchParams({ challenge })}`);
}

/** Le verifier de la connexion en cours dans cet onglet, retiré : il ne sert qu'une fois */
export function takeVerifier(): string | null {
  const verifier = sessionStorage.getItem(VERIFIER_STORAGE_KEY);
  sessionStorage.removeItem(VERIFIER_STORAGE_KEY);
  return verifier;
}

/** Empreinte S256 (RFC 7636) : base64url du SHA-256 */
export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
